// src/pages/GroceryProducts.jsx
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
} from "firebase/firestore";

const GroceryProducts = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [form, setForm] = useState({
        name: "",
        categoryId: "",
        price: "",
        unit: "",
        imageUrl: "",
        stock: "",
    });
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchCategories();
        fetchProducts();
    }, []);

    const fetchCategories = async () => {
        const snapshot = await getDocs(collection(db, "groceryCategories"));
        setCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const fetchProducts = async () => {
        const snapshot = await getDocs(collection(db, "groceryProducts"));
        setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name || !form.categoryId || !form.price) return;

        setLoading(true);
        try {
            if (editingId) {
                await updateDoc(doc(db, "groceryProducts", editingId), {
                    ...form,
                    price: Number(form.price),
                    stock: Number(form.stock),
                });
                setEditingId(null);
            } else {
                const docRef = await addDoc(collection(db, "groceryProducts"), {
                    ...form,
                    price: Number(form.price),
                    stock: Number(form.stock),
                });
                await updateDoc(doc(db, "groceryProducts", docRef.id), { id: docRef.id });
            }
            setForm({ name: "", categoryId: "", price: "", unit: "", imageUrl: "", stock: "" });
            fetchProducts();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (prod) => {
        setForm({
            name: prod.name,
            categoryId: prod.categoryId,
            price: prod.price,
            unit: prod.unit,
            imageUrl: prod.imageUrl,
            stock: prod.stock,
        });
        setEditingId(prod.id);
    };

    const handleDelete = async (id) => {
        const confirmed = window.confirm("Are you sure you want to delete this product?");
        if (!confirmed) return;
        await deleteDoc(doc(db, "groceryProducts", id));
        fetchProducts();
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto flex flex-col space-y-6 h-[90vh]">
                <h1 className="text-3xl font-bold">Grocery Products</h1>

                {/* Form Section */}
                <form
                    onSubmit={handleSave}
                    className="bg-white p-4 rounded shadow space-y-4"
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                            type="text"
                            placeholder="Product Name"
                            className="p-2 border rounded"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                        <select
                            value={form.categoryId}
                            onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                            className="p-2 border rounded"
                        >
                            <option value="">Select Category</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                        <input
                            type="number"
                            placeholder="Price"
                            className="p-2 border rounded"
                            value={form.price}
                            onChange={(e) => setForm({ ...form, price: e.target.value })}
                        />
                        <input
                            type="text"
                            placeholder="Unit (e.g., kg)"
                            className="p-2 border rounded"
                            value={form.unit}
                            onChange={(e) => setForm({ ...form, unit: e.target.value })}
                        />
                        <input
                            type="text"
                            placeholder="Stock"
                            className="p-2 border rounded"
                            value={form.stock}
                            onChange={(e) => setForm({ ...form, stock: e.target.value })}
                        />
                        <input
                            type="text"
                            placeholder="Image URL"
                            className="p-2 border rounded"
                            value={form.imageUrl}
                            onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                        />
                    </div>
                    {form.imageUrl && (
                        <img
                            src={form.imageUrl}
                            alt="Preview"
                            className="h-24 w-24 object-cover rounded border mt-2"
                        />
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                        {loading ? "Saving..." : editingId ? "Update Product" : "Add Product"}
                    </button>
                </form>

                {/* Scrollable Table Section */}
                <div className="flex-1 overflow-y-auto overflow-x-auto bg-white rounded shadow">
                    <table className="w-full border-collapse">
                        <thead className="bg-gray-100 sticky top-0 z-10">
                            <tr>
                                <th className="border p-2">Image</th>
                                <th className="border p-2">Name</th>
                                <th className="border p-2">Category</th>
                                <th className="border p-2">Price</th>
                                <th className="border p-2">Unit</th>
                                <th className="border p-2">Stock</th>
                                <th className="border p-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="p-4 text-center">No products found.</td>
                                </tr>
                            ) : (
                                products.map(prod => {
                                    const cat = categories.find(c => c.id === prod.categoryId);
                                    return (
                                        <tr key={prod.id} className="border-b">
                                            <td className="p-2 text-center">
                                                {prod.imageUrl && (
                                                    <img
                                                        src={prod.imageUrl}
                                                        alt={prod.name}
                                                        className="h-16 w-16 object-cover rounded mx-auto"
                                                    />
                                                )}
                                            </td>
                                            <td className="p-2">{prod.name}</td>
                                            <td className="p-2">{cat?.name || "N/A"}</td>
                                            <td className="p-2">{prod.price}</td>
                                            <td className="p-2">{prod.unit}</td>
                                            <td className="p-2">{prod.stock}</td>
                                            <td className="p-2 space-x-2">
                                                <button
                                                    onClick={() => handleEdit(prod)}
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(prod.id)}
                                                    className="text-red-600 hover:underline"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default GroceryProducts;
